import { useEffect, useRef, useState } from "react";
import ZoomVideo from "@zoom/videosdk";
import { loadDevicePrefs, saveDevicePrefs } from "./devicePrefs";

export type Device = { label: string; deviceId: string };

function isFrontCamera(d: Device) {
  const label = (d.label || "").toLowerCase();

  return (
    label.includes("front") ||
    label.includes("user") ||
    label.includes("selfie") ||
    label.includes("face")
  );
}

function getPreferredCameraId(cameras: Device[]) {
  const prefs = loadDevicePrefs();

  if (prefs.cameraId && cameras.some((d) => d.deviceId === prefs.cameraId)) {
    return prefs.cameraId;
  }

  return cameras.find(isFrontCamera)?.deviceId || cameras[0]?.deviceId || "";
}

export function usePreviewAV() {
  const audioRef = useRef(ZoomVideo.createLocalAudioTrack());
  const videoRef = useRef<any>(null);

  const [micList, setMicList] = useState<Device[]>([]);
  const [speakerList, setSpeakerList] = useState<Device[]>([]);
  const [cameraList, setCameraList] = useState<Device[]>([]);

  const [activeMicId, setActiveMicId] = useState("");
  const [activeSpeakerId, setActiveSpeakerId] = useState("");
  const [activeCameraId, setActiveCameraId] = useState("");

  const [isStartedAudio, setIsStartedAudio] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isStartedVideo, setIsStartedVideo] = useState(false);

  useEffect(() => {
    let mounted = true;

    (async () => {
      const all = await ZoomVideo.getDevices();
      if (!mounted) return;

      const cams = all
        .filter((d) => d.kind === "videoinput")
        .map((d) => ({ label: d.label, deviceId: d.deviceId }));

      const mics = all
        .filter((d) => d.kind === "audioinput")
        .map((d) => ({ label: d.label, deviceId: d.deviceId }));

      const spks = all
        .filter((d) => d.kind === "audiooutput")
        .map((d) => ({ label: d.label, deviceId: d.deviceId }));

      const preferredCameraId = getPreferredCameraId(cams);

      setCameraList(cams);
      setMicList(mics);
      setSpeakerList(spks);

      setActiveCameraId((v) => v || preferredCameraId);
      setActiveMicId((v) => v || mics[0]?.deviceId || "");
      setActiveSpeakerId((v) => v || spks[0]?.deviceId || "");
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const recreateVideoTrack = async (deviceId?: string) => {
    try {
      await videoRef.current?.stop();
    } catch {}

    videoRef.current = deviceId
      ? ZoomVideo.createLocalVideoTrack(deviceId)
      : ZoomVideo.createLocalVideoTrack();
  };

  const startVideo = async (el: HTMLVideoElement) => {
    const preferredCameraId = activeCameraId || getPreferredCameraId(cameraList);

    await recreateVideoTrack(preferredCameraId || undefined);
    await videoRef.current.start(el);

    setIsStartedVideo(true);

    if (preferredCameraId) {
      setActiveCameraId(preferredCameraId);
      saveDevicePrefs({ cameraId: preferredCameraId });
    }

    saveDevicePrefs({ videoOn: true });
  };

  const stopVideo = async () => {
    try {
      await videoRef.current?.stop();
    } catch {}

    setIsStartedVideo(false);
    saveDevicePrefs({ videoOn: false });
  };

  const toggleVideo = async (el?: HTMLVideoElement | null) => {
    if (isStartedVideo) return stopVideo();
    if (!el) return;
    return startVideo(el);
  };

  const startAudio = async () => {
    await audioRef.current.start();
    setIsStartedAudio(true);

    try {
      await audioRef.current.mute();
    } catch {}

    setIsMuted(true);
    saveDevicePrefs({ micMuted: true });
  };

  const toggleMute = async () => {
    if (!isStartedAudio) {
      await startAudio();
      return;
    }

    if (isMuted) {
      await audioRef.current.unmute();
      setIsMuted(false);
      saveDevicePrefs({ micMuted: false });
    } else {
      await audioRef.current.mute();
      setIsMuted(true);
      saveDevicePrefs({ micMuted: true });
    }
  };

  const switchMicrophone = async (deviceId: string) => {
    const wasStarted = isStartedAudio;

    try {
      await audioRef.current.stop();
    } catch {}

    audioRef.current = ZoomVideo.createLocalAudioTrack(deviceId);

    setActiveMicId(deviceId);
    saveDevicePrefs({ micId: deviceId });

    setIsStartedAudio(false);
    setIsMuted(true);

    if (wasStarted) await startAudio();
  };

  const switchCamera = async (deviceId: string) => {
    const wasStarted = isStartedVideo;

    try {
      if (videoRef.current && wasStarted) {
        await videoRef.current.switchCamera(deviceId);
      } else {
        await recreateVideoTrack(deviceId);
      }
    } catch (e) {
      console.warn("preview switchCamera failed", e);

      await recreateVideoTrack(deviceId);
    }

    setActiveCameraId(deviceId);
    saveDevicePrefs({ cameraId: deviceId });
  };

  const selectSpeaker = (deviceId: string) => {
    setActiveSpeakerId(deviceId);
    saveDevicePrefs({ speakerId: deviceId });
  };

  const stopAll = async () => {
    try {
      await audioRef.current.stop();
    } catch {}

    try {
      await videoRef.current?.stop();
    } catch {}

    setIsStartedAudio(false);
    setIsMuted(true);
    setIsStartedVideo(false);
  };

  useEffect(() => {
    return () => {
      void stopAll();
    };
  }, []);

  return {
    micList,
    speakerList,
    cameraList,
    activeMicId,
    activeSpeakerId,
    activeCameraId,
    isStartedAudio,
    isMuted,
    isStartedVideo,
    toggleMute,
    toggleVideo,
    switchMicrophone,
    switchCamera,
    selectSpeaker,
    stopAll,
  };
}